import { useState } from "react";
import type { PronunciationResult } from "../lib/api";

interface Props {
  result: PronunciationResult;
}

function scoreColor(score: number): string {
  if (score >= 85) return "text-emerald-300";
  if (score >= 70) return "text-amber-300";
  return "text-rose-300";
}
function scoreBg(score: number): string {
  if (score >= 85) return "border-emerald-500/40 bg-emerald-500/10";
  if (score >= 70) return "border-amber-500/40 bg-amber-500/10";
  return "border-rose-500/40 bg-rose-500/10";
}

export function PronunciationBubble({ result }: Props): JSX.Element | null {
  const [open, setOpen] = useState(false);

  if (!result || !Array.isArray(result.words) || result.words.length === 0) return null;

  const weak = result.words.filter((w) => w.score < 75);
  const overall = result.overall;

  return (
    <div className="mt-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-2 rounded-full border px-2.5 py-0.5 text-xs ${scoreBg(overall)} hover:opacity-90`}
      >
        <span aria-hidden>{"🎤"}</span>
        <span className={`font-mono font-semibold ${scoreColor(overall)}`}>{overall}</span>
        <span className="text-zinc-400">pronunciation</span>
        {weak.length > 0 && (
          <span className="text-zinc-400">
            {"·"} {weak.length} weak
          </span>
        )}
        <span className="text-zinc-400">{open ? "▴" : "▾"}</span>
      </button>

      {open && (
        <div className="mt-2 rounded-lg border border-zinc-700/60 bg-zinc-900/60 p-3 text-xs space-y-2">
          <div className="flex flex-wrap gap-1.5">
            {result.words.map((w, i) => (
              <span
                key={i}
                className={`inline-flex items-baseline gap-1 rounded border px-1.5 py-0.5 ${scoreBg(w.score)}`}
                title={
                  w.issues.length > 0
                    ? w.issues.map((p) => `${p.phone} (${p.score})${p.sounded_like ? ` → ${p.sounded_like}` : ""}`).join(", ")
                    : `score ${w.score}`
                }
              >
                <span className={`font-medium ${scoreColor(w.score)}`}>{w.word}</span>
                <span className="font-mono text-[10px] text-zinc-400">{w.score}</span>
              </span>
            ))}
          </div>

          {weak.length > 0 && (
            <div className="space-y-1">
              <div className="text-zinc-400">Phonemes to polish:</div>
              <ul className="space-y-1">
                {weak.slice(0, 5).map((w, i) => (
                  <li key={i} className="rounded border border-zinc-700 px-2 py-1">
                    <span className="font-medium text-zinc-100">{w.word}</span>
                    <span className="text-zinc-500 font-mono ml-2">{w.score}</span>
                    {w.issues.length > 0 && (
                      <div className="text-zinc-400 mt-0.5">
                        {w.issues.map((p, j) => (
                          <span key={j} className="inline-block mr-2">
                            <span className="font-mono text-rose-300">/{p.phone}/</span>
                            {p.sounded_like && (
                              <>
                                {" "}sounded like{" "}
                                <span className="font-mono text-amber-300">/{p.sounded_like}/</span>
                              </>
                            )}
                          </span>
                        ))}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-wrap gap-3 text-[10px] text-zinc-500 pt-1 border-t border-zinc-800">
            {result.ielts_pronunciation != null && (
              <span>IELTS pron band: <span className="text-zinc-200">{result.ielts_pronunciation}</span></span>
            )}
            {result.cefr_pronunciation && (
              <span>CEFR: <span className="text-zinc-200">{result.cefr_pronunciation}</span></span>
            )}
            {result.speech_rate != null && (
              <span>{result.speech_rate} wpm</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
